import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, LogOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { STRINGS } from "@/lib/i18n";
import { scrollToId } from "@/lib/scroll";

export default function Navbar({ onOpenAuth }) {
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const t = STRINGS[lang].nav;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const LINKS = [
    { label: t.formats, href: "#formatos", testid: "nav-link-formatos" },
    { label: t.levels, href: "#niveis", testid: "nav-link-niveis" },
    { label: t.styles, href: "#estilos", testid: "nav-link-estilos" },
    { label: t.professor, href: "#professor", testid: "nav-link-professor" },
    { label: t.book, href: "#agendar", testid: "nav-link-agendar" },
    { label: t.contact, href: "#contato", testid: "nav-link-contato" },
  ];

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0B101C]/70 backdrop-blur-xl"
      data-testid="main-navbar"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button onClick={() => go("#topo")} className="text-left" data-testid="nav-home-button">
          <Logo />
        </button>

        <nav className="hidden items-center gap-7 lg:flex">
          {LINKS.map((link) => (
            <button
              key={link.href}
              onClick={() => go(link.href)}
              data-testid={link.testid}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors duration-300 hover:text-blue-400"
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LanguageSwitcher />
          {user ? (
            <>
              <button
                onClick={() => navigate("/portal")}
                data-testid="nav-portal-button"
                className="rounded-full border border-blue-500/40 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-blue-300 transition-colors duration-300 hover:bg-blue-500/10"
              >
                {t.studentArea}
              </button>
              <button
                onClick={logout}
                data-testid="nav-logout-button"
                aria-label={t.logout}
                className="rounded-full p-2 text-slate-500 transition-colors duration-300 hover:text-blue-400"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <button
              onClick={onOpenAuth}
              data-testid="nav-login-button"
              className="rounded-full bg-blue-400 px-5 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#05070D] transition-colors duration-300 hover:bg-blue-300"
            >
              {t.studentArea}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LanguageSwitcher />
          <button
            className="rounded-md p-2 text-slate-300"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/5 bg-[#05070D]/95 px-6 py-6 backdrop-blur-xl lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-5">
            {LINKS.map((link) => (
              <button
                key={link.href}
                onClick={() => go(link.href)}
                data-testid={`${link.testid}-mobile`}
                className="text-left font-mono text-xs uppercase tracking-[0.22em] text-slate-300"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                if (user) navigate("/portal");
                else onOpenAuth();
              }}
              data-testid="nav-login-button-mobile"
              className="mt-2 w-fit rounded-full bg-blue-400 px-5 py-2 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-[#05070D]"
            >
              {t.studentArea}
            </button>
          </div>
        </div>
      )}
    </motion.header>
  );
}
